import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSafe } from '../context/SafeContext';
import { isBiometricUnlockSupported } from '../crypto/biometricUnlock';
import { HbsInput } from '../components/hbs/Input';
import { HbsButton } from '../components/hbs/Button';
import { HeartLogo } from '../components/hbs/HeartLogo';
import { Icon } from '../components/hbs/Icon';
import { EyeToggle } from '../components/hbs/EyeToggle';

/** Best-effort label for the platform biometric — cosmetic only. */
function biometricLabel(): string {
  const p = typeof navigator !== 'undefined' ? `${navigator.platform} ${navigator.userAgent}` : '';
  if (/Win/i.test(p)) return 'Windows Hello';
  if (/Mac|iPhone|iPad/i.test(p)) return 'Touch ID';
  return 'biometrics';
}

export default function UnlockPage(): JSX.Element {
  const navigate = useNavigate();
  const { unlock, unlockWithBiometric, biometricEnrolled, logout, loadingStep } = useSafe();

  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [bioReady, setBioReady] = useState(false);
  const [bioLoading, setBioLoading] = useState(false);
  // Fire the auto-attempt at most once per mount, so cancelling doesn't re-prompt.
  const autoAttempted = useRef(false);

  const label = biometricLabel();

  useEffect(() => {
    // Show + auto-attempt biometric unlock only when enrolled AND the platform
    // has a usable authenticator. Mirrors the extension's UnlockPage.
    if (!biometricEnrolled) return;
    let cancelled = false;

    // Only auto-prompt while the tab is visible. Switching tabs auto-locks the
    // safe (SafeContext visibilitychange handler), which mounts this page in a
    // background tab; issuing a WebAuthn request there leaves a stuck
    // "no passkeys available" prompt and a tab notification badge. If the tab is
    // hidden, defer the auto-attempt until it next becomes visible.
    const attemptWhenVisible = () => {
      if (cancelled || autoAttempted.current) return;
      if (document.visibilityState !== 'visible') return;
      autoAttempted.current = true;
      document.removeEventListener('visibilitychange', attemptWhenVisible);
      void handleBiometric();
    };

    void isBiometricUnlockSupported().then((ok) => {
      if (cancelled || !ok) return;
      setBioReady(true);
      if (document.visibilityState === 'visible') {
        attemptWhenVisible();
      } else {
        document.addEventListener('visibilitychange', attemptWhenVisible);
      }
    });

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', attemptWhenVisible);
    };
    // handleBiometric closes over stable callbacks; run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [biometricEnrolled]);

  async function handleBiometric(): Promise<void> {
    setError('');
    setBioLoading(true);
    try {
      const result = await unlockWithBiometric();
      if (result.ok) return; // router swaps to the unlocked app
      if (result.code === 'USER_CANCELED') return; // stay put, silently
      if (result.code === 'STALE') {
        setBioReady(false);
        setError(result.error ?? 'Biometric unlock is no longer set up. Enter your safe password.');
      } else {
        setError(result.error ?? 'Biometric unlock failed. Enter your safe password.');
      }
    } finally {
      setBioLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await unlock(password);
      if (!result.ok) {
        setError(result.error ?? 'Incorrect safe password');
        setPassword('');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSignOut(): Promise<void> {
    await logout();
    navigate('/auth', { replace: true });
  }

  return (
    <div className="min-h-screen w-full bg-hbs-bg flex items-center justify-center">
      <div className="w-full max-w-sm bg-hbs-surface rounded-hbs-xl border border-hbs-border p-8 shadow-hbs-lg">
        <div className="flex flex-col items-center gap-3 mb-6">
          <HeartLogo size={104} />
          <h1 className="font-display text-[22px] tracking-hbs-tight text-hbs-text text-center">
            Your safe is locked
          </h1>
          <p className="text-[13.5px] text-hbs-text-mute text-center max-w-[280px]">
            Enter your safe password to continue.
          </p>
        </div>

        {bioReady && (
          <div className="flex flex-col gap-3 mb-4">
            <HbsButton
              type="button"
              id="unlock-biometric-button"
              onClick={handleBiometric}
              loading={bioLoading}
              fullWidth
              leadingIcon={<Icon name="fingerprint" size={18} />}
            >
              {`Unlock with ${label}`}
            </HbsButton>
            <div className="flex items-center gap-2" aria-hidden="true">
              <span className="h-px flex-1 bg-hbs-border" />
              <span className="text-[11px] text-hbs-text-mute">or safe password</span>
              <span className="h-px flex-1 bg-hbs-border" />
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <HbsInput
            label="Safe password"
            type={revealed ? 'text' : 'password'}
            value={password}
            onChange={setPassword}
            autoFocus
            required
            mono={revealed}
            leadingIcon={<Icon name="key" size={16} className="text-hbs-accent" />}
            trailingActions={<EyeToggle revealed={revealed} onChange={setRevealed} />}
          />

          {error && (
            <p className="text-[13px] text-hbs-danger bg-hbs-danger/10 rounded-hbs-sm p-3" role="alert">
              {error}
            </p>
          )}

          <HbsButton id="unlock-submit-button" type="submit" loading={loading} disabled={!password} fullWidth>
            {loading && loadingStep ? loadingStep : 'Unlock'}
          </HbsButton>

          <HbsButton type="button" variant="plain" id="unlock-sign-out-button" onClick={handleSignOut}>
            Sign out / Switch account
          </HbsButton>
        </form>

        <p className="text-[11px] text-hbs-text-mute text-right mt-6" aria-label="Build identifier">
          v{__APP_VERSION__} ({__GIT_SHA__})
        </p>
      </div>
    </div>
  );
}
